import { type LucideIcon, TrendingDown, TrendingUp } from "lucide-react";
import { cn } from "@/lib/utils";

// ─────────────────────────────────────────────
// StatCard
//
// KPI card with icon, label, large mono value,
// and optional delta / trend indicator.
// Design: §9.2 Stat Cards
// ─────────────────────────────────────────────

interface StatCardProps {
  icon?: LucideIcon;
  label: string;
  value: string | number;
  /** e.g. "+12%" or "-3" */
  delta?: string;
  /** direction of the trend for color coding */
  trend?: "up" | "down" | "neutral";
  /** override the value color (defaults to accent-light) */
  valueColor?: string;
  className?: string;
}

export function StatCard({
  icon: Icon,
  label,
  value,
  delta,
  trend,
  valueColor,
  className,
}: StatCardProps) {
  const trendColor =
    trend === "up"
      ? "text-success"
      : trend === "down"
        ? "text-danger"
        : "text-text-muted";

  const TrendIcon = trend === "up" ? TrendingUp : TrendingDown;

  return (
    <div
      className={cn(
        "flex flex-col gap-3 rounded-card border border-border-default bg-card p-5",
        className
      )}
    >
      {/* Header: icon + label */}
      <div className="flex items-center gap-inner">
        {Icon && (
          <div className="flex h-8 w-8 items-center justify-center rounded-control bg-hover">
            <Icon className="h-4 w-4 text-text-muted" />
          </div>
        )}
        <span className="text-[14px] font-medium text-text-muted">{label}</span>
      </div>

      {/* Value */}
      <div className="flex items-end gap-2">
        <span
          className="font-mono text-[32px] font-medium leading-none text-accent-light"
          style={valueColor ? { color: valueColor } : undefined}
        >
          {value}
        </span>

        {/* Delta / trend */}
        {delta && (
          <div className={cn("mb-0.5 flex items-center gap-1 text-[12px] font-medium", trendColor)}>
            {trend && trend !== "neutral" && <TrendIcon className="h-3 w-3" />}
            <span>{delta}</span>
          </div>
        )}
      </div>
    </div>
  );
}
